import { motion } from "framer-motion";
import { PersonStanding } from "lucide-react";

const TECH_NODES = [
  "AI",
  "SOFTWARE",
  "AUTOMATION",
  "DATA",
  "DIGITAL COMMERCE",
  "MOBILE APPS",
  "CLOUD",
  "ANALYTICS",
];

const COLORS = ["#FF8800", "#00B0FF", "#00E676"];

export function TechHuman() {
  return (
    <section data-testid="tech-human-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,176,255,0.06),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">Technology + Human Potential</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="tech-human-headline">
            TECHNOLOGY DOESN'T REPLACE PEOPLE.
            <br />
            <span className="text-gradient-saffron">IT MULTIPLIES THEM.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
            Humans stay at the center. Technology becomes the engine around them — helping
            entrepreneurs build faster, smarter and further than ever before.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="relative mt-10 h-[380px] sm:h-[460px] flex items-center justify-center"
          data-testid="tech-human-orbit"
        >
          {/* Center human */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full glass flex items-center justify-center shadow-[0_0_70px_rgba(255,136,0,0.3)] animate-float">
              <PersonStanding className="w-10 h-10 sm:w-12 sm:h-12 text-[#FF8800]" strokeWidth={1.6} />
            </div>
            <p className="mt-4 font-mono-tech text-[10px] tracking-[0.35em] text-slate-300 uppercase">The Entrepreneur</p>
          </div>

          {/* Orbiting technology ring */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="relative w-[320px] h-[320px] sm:w-[520px] sm:h-[520px]">
              <div className="orbit-ring absolute inset-0 rounded-full border border-[#00B0FF]/15">
                {TECH_NODES.map((n, i) => {
                  const deg = (360 / TECH_NODES.length) * i;
                  const color = COLORS[i % 3];
                  return (
                    <div key={n} className="absolute inset-0" style={{ transform: `rotate(${deg}deg)` }}>
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <div className="orbit-chip">
                          <span
                            className="glass inline-block px-3 py-1.5 rounded-full font-mono-tech text-[9px] sm:text-[10px] tracking-[0.18em] whitespace-nowrap"
                            style={{ color, borderColor: `${color}44`, transform: `rotate(${-deg}deg)` }}
                            data-testid={`tech-node-${n.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {n}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="absolute inset-[15%] rounded-full border border-dashed border-white/10 orbit-ring-2" />
              <div className="absolute inset-[30%] rounded-full border border-white/5" />
            </div>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-6 text-center text-lg sm:text-xl font-display font-semibold text-slate-200"
          data-testid="tech-human-statement"
        >
          TECHNOLOGY MULTIPLIES <span className="text-gradient-saffron">HUMAN POTENTIAL.</span>
        </motion.p>
      </div>
    </section>
  );
}
